import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { useDispatch, useSelector } from "react-redux";
import {
  getOrgBudgetAlerts,
  createOrgBudgetAlert,
  updateOrgBudgetAlert,
  deleteOrgBudgetAlert,
  getOrgBudgetScheduledReports,
  createOrgBudgetScheduledReport,
  updateOrgBudgetScheduledReport,
  deleteOrgBudgetScheduledReport,
  clearBudgetMessages,
} from "../../../store/spendCategorization/budgetSlice";
import { useUsers } from "../../../components/Common/HelperComponents/GetDataFromStore";

const OrgBudgetAlertsModal = ({
  open,
  onClose,
  orgBudgetId,
  orgBudgetName,
}) => {
  const dispatch = useDispatch();
  const { users } = useUsers();
  
  // Redux state
  const {
    orgBudgetAlerts,
    isFetchingOrgBudgetAlerts,
    fetchOrgBudgetAlertsError,
    isCreatingOrgBudgetAlert,
    createOrgBudgetAlertSuccess,
    createOrgBudgetAlertError,
    isUpdatingOrgBudgetAlert,
    updateOrgBudgetAlertSuccess,
    updateOrgBudgetAlertError,
    isDeletingOrgBudgetAlert,
    deleteOrgBudgetAlertSuccess,
    deleteOrgBudgetAlertError,
    orgBudgetScheduledReports,
    isFetchingOrgBudgetScheduledReports,
    fetchOrgBudgetScheduledReportsError,
    isCreatingOrgBudgetScheduledReport,
    createOrgBudgetScheduledReportSuccess,
    createOrgBudgetScheduledReportError,
    isUpdatingOrgBudgetScheduledReport,
    updateOrgBudgetScheduledReportSuccess,
    updateOrgBudgetScheduledReportError,
    isDeletingOrgBudgetScheduledReport,
    deleteOrgBudgetScheduledReportSuccess,
    deleteOrgBudgetScheduledReportError,
  } = useSelector((state) => state.budget);

  // Local state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0 for Threshold Alerts, 1 for Scheduled Reports
  const [formData, setFormData] = useState({
    threshold_percent: "",
    interval_days: "",
    notify_emails: [],
  });
  const [errors, setErrors] = useState({});
  const [showUpdatedColumns, setShowUpdatedColumns] = useState(true);

  // Fetch alerts and scheduled reports when modal opens
  useEffect(() => {
    if (open && orgBudgetId) {
      dispatch(getOrgBudgetAlerts(orgBudgetId));
      dispatch(getOrgBudgetScheduledReports(orgBudgetId));
    }
  }, [open, orgBudgetId, dispatch]);

  // Clear messages when modal closes
  useEffect(() => {
    if (!open) {
      dispatch(clearBudgetMessages());
      setCreateModalOpen(false);
      setEditModalOpen(false);
      setDeleteDialogOpen(false);
      setSelectedAlert(null);
      setActiveTab(0);
      setFormData({ threshold_percent: "", interval_days: "", notify_emails: [] });
      setErrors({});
    }
  }, [open, dispatch]);

  // Handle success messages
  useEffect(() => {
    if (createOrgBudgetAlertSuccess) {
      setCreateModalOpen(false);
      setFormData({ threshold_percent: "", notify_emails: [] });
      setErrors({});
      dispatch(getOrgBudgetAlerts(orgBudgetId));
    }
  }, [createOrgBudgetAlertSuccess, orgBudgetId, dispatch]);

  useEffect(() => {
    if (updateOrgBudgetAlertSuccess) {
      setEditModalOpen(false);
      setSelectedAlert(null);
      setFormData({ threshold_percent: "", notify_emails: [] });
      setErrors({});
      dispatch(getOrgBudgetAlerts(orgBudgetId));
    }
  }, [updateOrgBudgetAlertSuccess, orgBudgetId, dispatch]);

  useEffect(() => {
    if (deleteOrgBudgetAlertSuccess) {
      setDeleteDialogOpen(false);
      setSelectedAlert(null);
      dispatch(getOrgBudgetAlerts(orgBudgetId));
    }
  }, [deleteOrgBudgetAlertSuccess, orgBudgetId, dispatch]);

  // Handle scheduled reports success messages
  useEffect(() => {
    if (createOrgBudgetScheduledReportSuccess) {
      setCreateModalOpen(false);
      setFormData({ threshold_percent: "", interval_days: "", notify_emails: [] });
      setErrors({});
      dispatch(getOrgBudgetScheduledReports(orgBudgetId));
    }
  }, [createOrgBudgetScheduledReportSuccess, orgBudgetId, dispatch]);

  useEffect(() => {
    if (updateOrgBudgetScheduledReportSuccess) {
      setEditModalOpen(false);
      setSelectedAlert(null);
      setFormData({ threshold_percent: "", interval_days: "", notify_emails: [] });
      setErrors({});
      dispatch(getOrgBudgetScheduledReports(orgBudgetId));
    }
  }, [updateOrgBudgetScheduledReportSuccess, orgBudgetId, dispatch]);

  useEffect(() => {
    if (deleteOrgBudgetScheduledReportSuccess) {
      setDeleteDialogOpen(false);
      setSelectedAlert(null);
      dispatch(getOrgBudgetScheduledReports(orgBudgetId));
    }
  }, [deleteOrgBudgetScheduledReportSuccess, orgBudgetId, dispatch]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCreateAlert = () => {
    setFormData({ threshold_percent: "", interval_days: "", notify_emails: [] });
    setErrors({});
    setCreateModalOpen(true);
  };

  const handleEditAlert = (alert) => {
    setSelectedAlert(alert);
    setFormData({
      threshold_percent: alert.threshold_percent.toString(),
      notify_emails: alert.notify_emails || [],
    });
    setErrors({});
    setEditModalOpen(true);
  };

  const handleDeleteAlert = (alert) => {
    setSelectedAlert(alert);
    setDeleteDialogOpen(true);
  };

  const handleEditScheduledReport = (report) => {
    setSelectedAlert(report);
    setFormData({
      interval_days: report.interval_days.toString(),
      notify_emails: report.notify_emails || [],
    });
    setErrors({});
    setEditModalOpen(true);
  };

  const handleDeleteScheduledReport = (report) => {
    setSelectedAlert(report);
    setDeleteDialogOpen(true);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (activeTab === 0) {
      // Threshold Alerts validation
      if (!formData.threshold_percent || formData.threshold_percent === "") {
        newErrors.threshold_percent = "Threshold percentage is required";
      } else {
        const threshold = parseFloat(formData.threshold_percent);
        if (isNaN(threshold) || threshold <= 0 || threshold > 100) {
          newErrors.threshold_percent = "Threshold must be between 1 and 100";
        }
      }
    } else {
      // Scheduled Reports validation
      if (!formData.interval_days) {
        newErrors.interval_days = "Interval days is required";
      } else {
        const interval = parseInt(formData.interval_days);
        if (isNaN(interval) || interval <= 0 || interval > 365) {
          newErrors.interval_days = "Interval must be between 1 and 365 days";
        }
      }
    }

    // Validate recipients
    if (!formData.notify_emails || formData.notify_emails.length === 0) {
      newErrors.notify_emails = "At least one recipient is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (activeTab === 0) {
      // Threshold Alerts
      const payload = {
        org_budget_id: orgBudgetId,
        threshold_percent: parseFloat(formData.threshold_percent),
        notify_emails: formData.notify_emails,
      };

      if (editModalOpen && selectedAlert) {
        dispatch(updateOrgBudgetAlert({
          alertId: selectedAlert.id,
          ...payload,
        }));
      } else {
        dispatch(createOrgBudgetAlert(payload));
      }
    } else {
      // Scheduled Reports
      const payload = {
        org_budget_id: orgBudgetId,
        interval_days: parseInt(formData.interval_days),
        notify_emails: formData.notify_emails,
      };

      if (editModalOpen && selectedAlert) {
        dispatch(updateOrgBudgetScheduledReport({
          reportId: selectedAlert.id,
          ...payload,
        }));
      } else {
        dispatch(createOrgBudgetScheduledReport(payload));
      }
    }
  };

  const handleConfirmDelete = () => {
    if (selectedAlert) {
      if (activeTab === 0) {
        dispatch(deleteOrgBudgetAlert(selectedAlert.id));
      } else {
        dispatch(deleteOrgBudgetScheduledReport(selectedAlert.id));
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };


  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="org-budget-alerts-modal-title"
        aria-describedby="org-budget-alerts-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: "90%", md: "80%" },
            maxWidth: "1200px",
            maxHeight: "90vh",
            bgcolor: "background.paper",
            borderRadius: "12px",
            boxShadow: 24,
            p: 0,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 3,
              borderBottom: "1px solid #e2e8f0",
              backgroundColor: "#f8fafc",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 4px rgba(245, 158, 11, 0.3)",
                }}
              >
                <NotificationsIcon sx={{ fontSize: 20, color: "white" }} />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#1e293b",
                    fontSize: "1.125rem",
                  }}
                >
                  Create Alerts ({orgBudgetName})
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748b",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                >
                  Manage budget threshold alerts and notifications
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateAlert}
                disabled={activeTab === 0 ? orgBudgetAlerts.length >= 3 : orgBudgetScheduledReports.length >= 3}
                sx={{
                  backgroundColor: "#3b82f6",
                  "&:hover": {
                    backgroundColor: "#2563eb",
                  },
                  "&:disabled": {
                    backgroundColor: "#e5e7eb",
                    color: "#9ca3af",
                  },
                  textTransform: "none",
                  fontWeight: 500,
                }}
              >
                {activeTab === 0 ? "Create Alert" : "Create Report"}
              </Button>
              <IconButton
                onClick={onClose}
                sx={{
                  color: "#64748b",
                  "&:hover": {
                    backgroundColor: "#f1f5f9",
                    color: "#374151",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Information Banner */}
          <Box sx={{ 
            px: 3, 
            py: 2, 
            backgroundColor: "#f8fafc", 
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            gap: 1
          }}>
            <Box sx={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              backgroundColor: "#3b82f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            }}>
              <Typography variant="caption" sx={{ color: "white", fontWeight: 600, fontSize: "0.75rem" }}>
                i
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.875rem" }}>
              You can configure up to 3 threshold alerts and 3 scheduled reports per organization budget.
            </Typography>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="org budget alerts tabs">
              <Tab label="Threshold Alerts" />
              <Tab label="Scheduled Reports" />
            </Tabs>
          </Box>

          {/* Content */}
          <Box sx={{ p: 3, maxHeight: "calc(90vh - 120px)", overflow: "auto" }}>
            {/* Tab Panel 0: Threshold Alerts */}
            {activeTab === 0 && (
              <>
                {/* Error Messages */}
                {fetchOrgBudgetAlertsError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {fetchOrgBudgetAlertsError}
                  </Alert>
                )}

                {/* Loading State */}
                {isFetchingOrgBudgetAlerts && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                  </Box>
                )}

                {/* Alerts Table */}
                {!isFetchingOrgBudgetAlerts && (
              <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid #e2e8f0" }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Threshold</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Recipients</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Created</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Created By</TableCell>
                      {showUpdatedColumns && (
                        <>
                          <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Updated</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Updated By</TableCell>
                        </>
                      )}
                      <TableCell sx={{ fontWeight: 600, color: "#374151", textAlign: "center" }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orgBudgetAlerts && orgBudgetAlerts.length > 0 ? (
                      orgBudgetAlerts.map((alert) => (
                        <TableRow key={alert.id} hover>
                          <TableCell>
                            <Chip
                              label={`${alert.threshold_percent}%`}
                              sx={{
                                backgroundColor: "#fef3c7",
                                color: "#92400e",
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              {alert.notify_emails && alert.notify_emails.length > 0 ? (
                                <>
                                  {alert.notify_emails.slice(0, 2).map((email, index) => (
                                    <Chip
                                      key={index}
                                      label={email}
                                      size="small"
                                      sx={{
                                        backgroundColor: "#e0f2fe",
                                        color: "#0369a1",
                                        fontSize: "0.75rem",
                                      }}
                                    />
                                  ))}
                                  {alert.notify_emails.length > 2 && (
                                    <Chip
                                      label={`+${alert.notify_emails.length - 2} more`}
                                      size="small"
                                      sx={{
                                        backgroundColor: "#f3f4f6",
                                        color: "#6b7280",
                                        fontSize: "0.75rem",
                                      }}
                                    />
                                  )}
                                </>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No recipients
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(alert.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {alert.created_by || "N/A"}
                            </Typography>
                          </TableCell>
                          {showUpdatedColumns && (
                            <>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {formatDate(alert.updated_at)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {alert.updated_by || "—"}
                                </Typography>
                              </TableCell>
                            </>
                          )}
                          <TableCell sx={{ textAlign: "center" }}>
                            <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                              <Tooltip title="Edit Alert">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditAlert(alert)}
                                  sx={{
                                    color: "#3b82f6",
                                    "&:hover": { backgroundColor: "#dbeafe" },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Alert">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteAlert(alert)}
                                  sx={{
                                    color: "#ef4444",
                                    "&:hover": { backgroundColor: "#fee2e2" },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ textAlign: "center", py: 4 }}>
                          <Typography variant="body1" color="text.secondary">
                            No alerts configured for this budget
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Click "Create Alert" to set up budget threshold notifications
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
                )}
              </>
            )}

            {/* Tab Panel 1: Scheduled Reports */}
            {activeTab === 1 && (
              <>
                {/* Error Messages */}
                {fetchOrgBudgetScheduledReportsError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {fetchOrgBudgetScheduledReportsError}
                  </Alert>
                )}

                {/* Loading State */}
                {isFetchingOrgBudgetScheduledReports && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                  </Box>
                )}

                {/* Scheduled Reports Table */}
                {!isFetchingOrgBudgetScheduledReports && (
                  <TableContainer component={Paper} sx={{ borderRadius: "8px", overflow: "hidden", overflowX: "auto" }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                          <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Interval</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Next Run</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Recipients</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Created</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Created By</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Updated</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Updated By</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#374151", textAlign: "center" }}>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {orgBudgetScheduledReports.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} sx={{ textAlign: "center", py: 4, color: "#64748b" }}>
                              No scheduled reports configured for this organization budget
                            </TableCell>
                          </TableRow>
                        ) : (
                          orgBudgetScheduledReports.map((report) => (
                            <TableRow key={report.id} hover>
                              <TableCell>
                                <Chip
                                  label={`${report.interval_days} days`}
                                  sx={{
                                    backgroundColor: "#e0f2fe",
                                    color: "#0369a1",
                                    fontWeight: 600,
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                                {formatDate(report.next_run_at)}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {report.notify_emails && report.notify_emails.length > 0 ? (
                                    <>
                                      {report.notify_emails.slice(0, 2).map((email, index) => (
                                        <Chip
                                          key={index}
                                          label={email}
                                          size="small"
                                          sx={{
                                            backgroundColor: "#e0f2fe",
                                            color: "#0369a1",
                                            fontSize: "0.75rem",
                                          }}
                                        />
                                      ))}
                                      {report.notify_emails.length > 2 && (
                                        <Chip
                                          label={`+${report.notify_emails.length - 2} more`}
                                          size="small"
                                          sx={{
                                            backgroundColor: "#f3f4f6",
                                            color: "#6b7280",
                                            fontSize: "0.75rem",
                                          }}
                                        />
                                      )}
                                    </>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No recipients
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                                {formatDate(report.created_at)}
                              </TableCell>
                              <TableCell sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                                {report.created_by || "—"}
                              </TableCell>
                              <TableCell sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                                {formatDate(report.updated_at)}
                              </TableCell>
                              <TableCell sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                                {report.updated_by || "—"}
                              </TableCell>
                              <TableCell sx={{ textAlign: "center" }}>
                                <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditScheduledReport(report)}
                                    sx={{ color: "#3b82f6" }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteScheduledReport(report)}
                                    sx={{ color: "#ef4444" }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Create/Edit Alert Modal */}
      <Dialog
        open={createModalOpen || editModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setEditModalOpen(false);
          setFormData({ threshold_percent: "", notify_emails: [] });
          setErrors({});
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            overflow: "hidden",
          },
        }}
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle sx={{ p: 0 }}>
            <Box
              sx={{
                p: 3,
                borderBottom: "1px solid #e2e8f0",
                backgroundColor: "#f8fafc",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 4px rgba(245, 158, 11, 0.3)",
                }}
              >
                <NotificationsIcon sx={{ fontSize: 20, color: "white" }} />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#1e293b",
                    fontSize: "1.125rem",
                  }}
                >
                  {editModalOpen 
                    ? (activeTab === 0 ? "Edit Alert" : "Edit Scheduled Report")
                    : (activeTab === 0 ? "Create Alert" : "Create Scheduled Report")
                  }
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748b",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                >
                  {editModalOpen 
                    ? (activeTab === 0 ? "Update alert settings and recipients" : "Update scheduled report settings and recipients")
                    : (activeTab === 0 ? "Set up budget threshold notifications" : "Set up scheduled budget reports")
                  }
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
              {/* Error Messages */}
              {createOrgBudgetAlertError && (
                <Alert severity="error">{createOrgBudgetAlertError}</Alert>
              )}
              {updateOrgBudgetAlertError && (
                <Alert severity="error">{updateOrgBudgetAlertError}</Alert>
              )}
              {createOrgBudgetScheduledReportError && (
                <Alert severity="error">{createOrgBudgetScheduledReportError}</Alert>
              )}
              {updateOrgBudgetScheduledReportError && (
                <Alert severity="error">{updateOrgBudgetScheduledReportError}</Alert>
              )}

              {/* Threshold Percentage or Interval Days */}
              {activeTab === 0 ? (
                <TextField
                  label="Threshold Percentage"
                  type="number"
                  value={formData.threshold_percent}
                  onChange={(e) => handleInputChange("threshold_percent", e.target.value)}
                  error={!!errors.threshold_percent}
                  helperText={errors.threshold_percent || "Enter a value between 1 and 100"}
                  inputProps={{ min: 1, max: 100, step: 0.1 }}
                  fullWidth
                  required
                />
              ) : (
                <TextField
                  label="Interval Days"
                  type="number"
                  value={formData.interval_days}
                  onChange={(e) => handleInputChange("interval_days", e.target.value)}
                  error={!!errors.interval_days}
                  helperText={errors.interval_days || "The period can be between 1 to 365 days"}
                  fullWidth
                  inputProps={{ min: 1, max: 365, step: 1 }}
                  required
                />
              )}

              {/* Recipients */}
              <FormControl fullWidth error={!!errors.notify_emails}>
                <InputLabel>Recipients</InputLabel>
                <Select
                  multiple
                  value={formData.notify_emails}
                  onChange={(e) => handleInputChange("notify_emails", e.target.value)}
                  input={<OutlinedInput label="Recipients" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: "60px", overflow: "auto" }}>
                      {selected.map((value) => (
                        <Chip 
                          key={value} 
                          label={value} 
                          size="small" 
                          sx={{ 
                            maxWidth: "200px",
                            "& .MuiChip-label": {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap"
                            }
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        width: "auto",
                        minWidth: 250,
                      },
                    },
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                  }}
                >
                  {users && users.length > 0 ? (
                    users.map((user) => (
                      <MenuItem 
                        key={user.username} 
                        value={user.username}
                        sx={{
                          py: 0.5,
                          "&:hover": {
                            backgroundColor: "#f3f4f6",
                          },
                        }}
                      >
                        <Checkbox 
                          checked={formData.notify_emails.indexOf(user.username) > -1} 
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <ListItemText 
                          primary={user.username}
                          primaryTypographyProps={{
                            fontSize: "0.875rem",
                            sx: {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "200px"
                            }
                          }}
                        />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No users available</MenuItem>
                  )}
                </Select>
                {errors.notify_emails && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.notify_emails}
                  </Typography>
                )}
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={() => {
                setCreateModalOpen(false);
                setEditModalOpen(false);
                setFormData({ threshold_percent: "", interval_days: "", notify_emails: [] });
                setErrors({});
              }}
              disabled={isCreatingOrgBudgetAlert || isUpdatingOrgBudgetAlert || isCreatingOrgBudgetScheduledReport || isUpdatingOrgBudgetScheduledReport}
              sx={{
                color: "#64748b",
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isCreatingOrgBudgetAlert || isUpdatingOrgBudgetAlert || isCreatingOrgBudgetScheduledReport || isUpdatingOrgBudgetScheduledReport}
              sx={{
                backgroundColor: "#3b82f6",
                "&:hover": { backgroundColor: "#2563eb" },
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                minWidth: "100px",
              }}
            >
              {isCreatingOrgBudgetAlert || isUpdatingOrgBudgetAlert || isCreatingOrgBudgetScheduledReport || isUpdatingOrgBudgetScheduledReport ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                editModalOpen 
                  ? (activeTab === 0 ? "Update" : "Update Report")
                  : (activeTab === 0 ? "Create" : "Create Report")
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedAlert(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              p: 3,
              borderBottom: "1px solid #e2e8f0",
              backgroundColor: "#f8fafc",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 4px rgba(239, 68, 68, 0.3)",
              }}
            >
              <DeleteIcon sx={{ fontSize: 20, color: "white" }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  fontSize: "1.125rem",
                }}
              >
                {activeTab === 0 ? "Delete Alert" : "Delete Scheduled Report"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#64748b",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                This action cannot be undone
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this {activeTab === 0 ? "alert" : "scheduled report"}?
          </Typography>
          {selectedAlert && (
            <Box sx={{ p: 2, backgroundColor: "#f8fafc", borderRadius: 1, border: "1px solid #e2e8f0" }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                {activeTab === 0 ? "Alert Details:" : "Report Details:"}
              </Typography>
              {activeTab === 0 ? (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Threshold: {selectedAlert.threshold_percent}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Recipients: {selectedAlert.notify_emails?.join(", ") || "None"}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Interval: {selectedAlert.interval_days} days
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Recipients: {selectedAlert.notify_emails?.join(", ") || "None"}
                  </Typography>
                </>
              )}
            </Box>
          )}
          {deleteOrgBudgetAlertError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteOrgBudgetAlertError}
            </Alert>
          )}
          {deleteOrgBudgetScheduledReportError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteOrgBudgetScheduledReportError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedAlert(null);
            }}
            disabled={isDeletingOrgBudgetAlert || isDeletingOrgBudgetScheduledReport}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={isDeletingOrgBudgetAlert || isDeletingOrgBudgetScheduledReport}
          >
            {isDeletingOrgBudgetAlert || isDeletingOrgBudgetScheduledReport ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              activeTab === 0 ? "Delete Alert" : "Delete Report"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrgBudgetAlertsModal;
